import { Eye, Plus, UploadCloud02 } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { getShortName } from 'src/utils/CommonFunctions';

import PickupNotesModal from './PickupNotesModal';
import SlickSliderLoaderRow from './SlickSliderLoaderRow';

interface SummaryRecord {
    orderDbId: number | null;
    orderId: string | null;
    // Leg identity — the backend sends it ONLY when the customer's Show-as-Multi-Leg is on,
    // so a card can distinguish sibling leg cards of the same master order.
    legId?: string | null;
    orderStatus: string | null;
    orderType: string | null;
    type: 'pickup' | 'delivery';
    tags: string[];
    scheduleTime: string | null;
    legStatus: string | null;
    orderLegId: number | null;
    carrier: {
        name: string;
        image: string | null;
    };
    pickup: {
        date: string | null;
        startTime: string | null;
        endTime: string | null;
        // Derived server-side from "both ends present" — NOT the order_leg_schedule
        // isPickupWindow/isDeliveryWindow columns, which nothing ever writes. The card
        // derives the same rule from endTime so it stays correct against an older API.
        isWindow: boolean;
        timezone: string | null;
        isAppointmentConfirmed: boolean;
    } | null;
    delivery: {
        date: string | null;
        startTime: string | null;
        endTime: string | null;
        isWindow: boolean;
        timezone: string | null;
        isAppointmentConfirmed: boolean;
    } | null;
    pickupNotes: string | null;
    deliveryNotes: string | null;
    shipperAddress1: string | null;
    shipperCity: string | null;
    shipperState: string | null;
    shipperPostal: string | null;
    shipperCountry: string | null;
    consigneeAddress1: string | null;
    consigneeCity: string | null;
    consigneeState: string | null;
    consigneePostal: string | null;
    consigneeCountry: string | null;
}

interface SlickSliderProps {
    records: SummaryRecord[];
    timezone: string;
    hasMore?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
    isLoading?: boolean;
}

/**
 * Format a scheduled appointment into a human-readable string.
 * date is a plain YYYY-MM-DD string in the record's timezone.
 * startTime/endTime are plain HH:mm:ss time strings (not UTC).
 * When both ends exist, shows a range: "Today, 9:00 AM - 11:00 AM"
 * Otherwise shows a point: "Today, at 9:00 AM"
 *
 * The window test is endTime presence, the same rule the order detail page
 * (TimeAndScheduling.tsx `hasTimeWindow`) and the tracking page (track.utils.ts `hasWindow`)
 * use — never the isWindow flag off order_leg_schedule, which nothing in the backend writes
 * and which therefore collapsed real 9:00-15:00 windows into "at 9:00 AM" on the card.
 */
const formatAppointmentTime = (date: string | null, startTime: string | null, endTime: string | null, recordTimezone: string | null, fallbackTimezone: string): string => {
    const dateStr = date ?? '';

    if (!dateStr && !startTime) return '-';

    const tz = recordTimezone || fallbackTimezone || 'UTC';

    const todayStr = moment().tz(tz).format('YYYY-MM-DD');
    const tomorrowStr = moment().add(1, 'day').tz(tz).format('YYYY-MM-DD');

    let dayLabel: string;

    if (dateStr === todayStr) {
        dayLabel = 'Today';
    } else if (dateStr === tomorrowStr) {
        dayLabel = 'Tomorrow';
    } else {
        // Full month + ordinal day, e.g. "August 3rd" (renders as "August 3rd, at 8:00 AM")
        dayLabel = moment(dateStr).format('MMMM Do');
    }

    if (!startTime) return dayLabel;

    const formattedStart = moment(startTime, 'HH:mm:ss').format('h:mm A');

    if (endTime) {
        const formattedEnd = moment(endTime, 'HH:mm:ss').format('h:mm A');

        return `${dayLabel}, ${formattedStart} - ${formattedEnd}`;
    }

    return `${dayLabel}, at ${formattedStart}`;
};

const getStatusBadgeType = (tags: string[], legStatus: string | null): 'success' | 'warning' | 'error' | 'info' => {
    if (tags.includes('running_late') || tags.includes('delayed')) return 'warning';
    if (legStatus === 'delivered') return 'success';

    return 'success';
};

const getStatusLabel = (tags: string[], legStatus: string | null, orderStatus: string | null): string => {
    if (tags.includes('running_late')) return 'Running Late';
    if (tags.includes('delayed')) return 'Delayed';

    const status = legStatus || orderStatus;

    if (status) return status.charAt(0).toUpperCase() + status.slice(1).replace(/-/g, ' ');

    return 'On Time';
};

const getOrderTypeBadgeType = (orderType: string | null): 'success' | 'lightblue' | 'gray' => {
    if (!orderType) return 'gray';
    const upper = orderType.toUpperCase();
    if (upper === 'LTL') return 'success';
    if (upper === 'FTL') return 'lightblue';

    return 'gray';
};

const SliderItem: React.FC<{ item: SummaryRecord; timezone: string; onNotesUpdate: (orderLegId: number, type: 'pickup' | 'delivery', notes: string) => void }> = ({
    item,
    timezone,
    onNotesUpdate,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const orgBranding = useOrgBranding();
    const addressRef = React.useRef<HTMLParagraphElement>(null);
    const appointmentRef = React.useRef<HTMLParagraphElement>(null);
    const carrierNameRef = React.useRef<HTMLParagraphElement>(null);
    const notesRef = React.useRef<HTMLParagraphElement>(null);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [imgError, setImgError] = useState(false);

    const isPickup = item.type === 'pickup';
    const schedule = isPickup ? item.pickup : item.delivery;
    // A stop with both ends is a window, not a single appointment moment — the heading says so
    // ("Delivery Window" / "Pickup Window") to match the range shown underneath it.
    const hasWindow = !!(schedule?.startTime && schedule?.endTime);
    const appointmentLabel = isPickup
        ? hasWindow
            ? t('Dashboard.UpcomingPickupDelivery.Pickup_Window', { defaultValue: 'Pickup Window' })
            : t('Dashboard.UpcomingPickupDelivery.Pickup_Appointment')
        : hasWindow
        ? t('Dashboard.UpcomingPickupDelivery.Delivery_Window', { defaultValue: 'Delivery Window' })
        : t('Dashboard.UpcomingPickupDelivery.Delivery_Appointment', { defaultValue: 'Delivery Appointment' });
    const addressLabel = isPickup ? t('Dashboard.UpcomingPickupDelivery.Pickup_Address') : t('Dashboard.UpcomingPickupDelivery.Delivery_Address', { defaultValue: 'Delivery Address' });
    const notesLabel = isPickup ? 'Pickup Notes' : 'Delivery Notes';
    const currentNotes = isPickup ? item.pickupNotes : item.deliveryNotes;

    const appointmentTime = formatAppointmentTime(schedule?.date ?? null, schedule?.startTime ?? null, schedule?.endTime ?? null, schedule?.timezone ?? null, timezone);

    // Build full address: "Address1, City, State Postal, Country"
    const buildFullAddress = (addr1: string | null, city: string | null, state: string | null, postal: string | null, country: string | null) => {
        const parts = [addr1, city, state && postal ? `${state} ${postal}` : state || postal, country];

        return parts.filter(Boolean).join(', ') || '-';
    };

    const addressDisplay = isPickup
        ? buildFullAddress(item.shipperAddress1, item.shipperCity, item.shipperState, item.shipperPostal, item.shipperCountry)
        : buildFullAddress(item.consigneeAddress1, item.consigneeCity, item.consigneeState, item.consigneePostal, item.consigneeCountry);

    const orderTypeLabel = item.orderType ? item.orderType.toUpperCase() : null;

    return (
        <>
            <div className="px-1.5 py-3">
                <div
                    className="rounded-xl border border-utilityGray200 overflow-hidden bg-white hover:shadow-lgc flex flex-col w-[344px] cursor-pointer"
                    onClick={() => item.orderDbId && navigate(`/orders/${item.orderDbId}`)}
                >
                    {/* Header: carrier logo + name + order ID */}
                    <div className="flex items-center gap-2 px-3 py-3 border-b border-utilityGray200 bg-gray50">
                        {item.carrier.image && !imgError ? (
                            <img
                                src={item.carrier.image}
                                alt={item.carrier.name}
                                className="w-[32px] h-[32px] min-w-[32px] min-h-[32px] border border-utilityBlack border-opacity-[0.08] bg-white rounded-md object-contain flex-none"
                                onError={() => setImgError(true)}
                            />
                        ) : item.carrier.name === orgBranding.name || item.carrier.name === 'Western Alliance' ? (
                            // Org presented as the carrier (masked or unassigned) with no uploaded brand logo — black WAL icon fallback
                            <div className="w-[32px] h-[32px] min-w-[32px] min-h-[32px] rounded-md bg-utilityBlack flex items-center justify-center p-1 flex-none">
                                <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                            </div>
                        ) : (
                            <div className="table-profile-customer-circle-xl rounded-md flex-none">{getShortName(item.carrier.name)}</div>
                        )}
                        <div className="flex flex-col min-w-0">
                            <TruncateTextCmp message={item.carrier.name} refName={carrierNameRef}>
                                <p ref={carrierNameRef} className="text-grayLight900 text-[12px] font-medium leading-[18px] truncate">
                                    {item.carrier.name}
                                </p>
                            </TruncateTextCmp>
                            <div className="flex items-center gap-x-1.5 min-w-0">
                                <p className="text-gray500 text-[12px] font-normal leading-[18px] truncate text-left">#{item.orderId ?? '-'}</p>
                                {/* Leg chip — only sent by the backend when Show-as-Multi-Leg is on,
                                    so sibling leg cards of the same master order are tellable apart */}
                                {item.legId && item.legId !== item.orderId && (
                                    <BadgeCmp style="modern" type="gray" size="xs" isHidePillDot mainClassName="flex-none bg-white">
                                        #{item.legId}
                                    </BadgeCmp>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Body: appointment + address + notes */}
                    <div className="space-y-3 flex-1 px-3 py-3">
                        {/* Appointment row with orderType badge */}
                        <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <h6 className="text-gray500 text-[12px] leading-[18px] font-normal mb-0.5">{appointmentLabel}</h6>
                                <TruncateTextCmp message={appointmentTime} refName={appointmentRef} tooltipType="default">
                                    <span className="text-textSecondary text-[12px] leading-[18px] font-medium block truncate" ref={appointmentRef}>
                                        {appointmentTime}
                                    </span>
                                </TruncateTextCmp>
                            </div>
                            {orderTypeLabel && (
                                <BadgeCmp style="modern" type={getOrderTypeBadgeType(item.orderType)} size="xs" mainClassName="flex-none bg-white">
                                    {orderTypeLabel}
                                </BadgeCmp>
                            )}
                        </div>

                        {/* Address */}
                        <div className="flex flex-col">
                            <h6 className="text-gray500 text-[12px] leading-[18px] font-normal mb-0.5">{addressLabel}</h6>
                            <TruncateTextCmp message={addressDisplay} refName={addressRef} tooltipType="action">
                                <p className="text-textSecondary text-[12px] leading-[18px] font-medium truncate" ref={addressRef}>
                                    {addressDisplay}
                                </p>
                            </TruncateTextCmp>
                        </div>

                        {/* Notes */}
                        <div className="flex flex-col">
                            <h6 className="text-gray500 text-[12px] leading-[18px] font-normal mb-0.5">{notesLabel}</h6>
                            {currentNotes ? (
                                <TruncateTextCmp message={currentNotes} refName={notesRef} tooltipType="default">
                                    <p className="text-textSecondary text-[12px] leading-[18px] font-semibold truncate" ref={notesRef}>
                                        {currentNotes}
                                    </p>
                                </TruncateTextCmp>
                            ) : (
                                <ButtonCmp
                                    className="flex w-fit gap-1 !text-[12px] !leading-[18px] text-primary !shadow-none"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowNotesModal(true);
                                    }}
                                    size="none"
                                    type="button"
                                    icon={<Plus className="w-4 h-4" />}
                                >
                                    Add Notes
                                </ButtonCmp>
                            )}
                        </div>
                    </div>

                    {/* Footer: status badge + eye icon */}
                    <div className="flex items-center justify-between px-3 py-3 border-t border-utilityGray200">
                        <BadgeCmp style="modern" type={getStatusBadgeType(item.tags, item.legStatus)}>
                            {getStatusLabel(item.tags, item.legStatus, item.orderStatus)}
                        </BadgeCmp>
                        <div className="flex space-x-2 !hidden">
                            <TooltipCmp message="Upload Customs Docs">
                                <UploadCloud02 className="w-4 h-4 cursor-pointer text-primary" />
                            </TooltipCmp>
                            <TooltipCmp message="View POD">
                                <Eye className="w-4 h-4 cursor-pointer text-primary" />
                            </TooltipCmp>
                        </div>
                    </div>
                </div>
            </div>

            {showNotesModal && item.orderLegId && (
                <PickupNotesModal
                    orderLegId={item.orderLegId}
                    noteType={item.type}
                    existingNotes={currentNotes}
                    onClose={() => setShowNotesModal(false)}
                    onSaved={(savedNotes) => {
                        onNotesUpdate(item.orderLegId!, item.type, savedNotes);
                        setShowNotesModal(false);
                    }}
                />
            )}
        </>
    );
};

export default function SlickSlider({ records, timezone, hasMore, onLoadMore, isLoadingMore, isLoading }: SlickSliderProps) {
    const [notesOverrides, setNotesOverrides] = useState<Record<string, string>>({});
    const sliderRef = useRef<Slider>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const isScrollingRef = useRef(false);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onWheel = (e: WheelEvent) => {
            e.preventDefault();

            if (isScrollingRef.current) return;

            isScrollingRef.current = true;

            if (e.deltaX > 0 || e.deltaY > 0) {
                sliderRef.current?.slickNext();
            } else {
                sliderRef.current?.slickPrev();
            }

            setTimeout(() => {
                isScrollingRef.current = false;
            }, 350);
        };

        el.addEventListener('wheel', onWheel, { passive: false });

        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    const handleNotesUpdate = (orderLegId: number, type: 'pickup' | 'delivery', notes: string) => {
        setNotesOverrides((prev) => ({ ...prev, [`${orderLegId}_${type}`]: notes }));
    };

    const getRecordWithNoteOverride = (item: SummaryRecord): SummaryRecord => {
        const key = `${item.orderLegId}_${item.type}`;

        if (notesOverrides[key] !== undefined) {
            return {
                ...item,
                pickupNotes: item.type === 'pickup' ? notesOverrides[key] : item.pickupNotes,
                deliveryNotes: item.type === 'delivery' ? notesOverrides[key] : item.deliveryNotes,
            };
        }

        return item;
    };

    const settings = {
        arrows: true,
        infinite: false,
        speed: 300,
        slidesToShow: 1,
        slidesToScroll: 1,
        swipeToSlide: true,
        variableWidth: true,
        afterChange: (currentSlide: number) => {
            if (onLoadMore && !isLoadingMore && currentSlide >= records.length - 5) {
                onLoadMore();
            }
        },
        responsive: [
            {
                breakpoint: 768,
                settings: { slidesToShow: 1, slidesToScroll: 1, swipeToSlide: true, variableWidth: true, arrows: false },
            },
        ],
    };

    if (isLoading) {
        return (
            <div ref={containerRef}>
                <Slider ref={sliderRef} className="custom-slick-slider-wrapper" {...settings}>
                    {Array.from({ length: 6 }).map((_, index) => (
                        <div key={index}>
                            <SlickSliderLoaderRow />
                        </div>
                    ))}
                </Slider>
            </div>
        );
    }

    return (
        <div ref={containerRef}>
            <Slider ref={sliderRef} className="custom-slick-slider-wrapper" {...settings}>
                {records.map((item, index) => (
                    <SliderItem key={`${item.orderId}-${item.orderLegId}-${item.type}-${index}`} item={getRecordWithNoteOverride(item)} timezone={timezone} onNotesUpdate={handleNotesUpdate} />
                ))}
                {(hasMore || isLoadingMore) && (
                    <div>
                        <SlickSliderLoaderRow />
                    </div>
                )}
            </Slider>
        </div>
    );
}
